/**
 * The Profil tab, whole: the read behind it, the name form, the photograph and the password.
 *
 * ONE hook rather than three, because all four halves share the trainer's id, one read of
 * `GET /personaltrainer/{id}` and `rehydrateSession()`. Split, the tab would hold three pieces of
 * state that only make sense together and thread the id and the announcer into each — three shallow
 * modules where the caller ends up knowing more, not less. The decisions are elsewhere:
 * `model/profilePatch`, `model/avatarCrop`, `assessPassword`, `entities/session`'s `updatePassword`.
 *
 * Every write ends in `rehydrateSession()`: the signed-in trainer is zustand state fed by
 * `GET /auth/me`, so invalidating a query key would leave the rail on the old name and face.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  rehydrateSession,
  updatePassword,
  useSessionUser,
  type PasswordUpdateOutcome,
} from '~/entities/session';
import { assessPassword, type PasswordAssessment } from '~/features/auth-by-email';
import {
  deleteAuthMeAvatar,
  putAuthMeAvatar,
} from '~/shared/api/generated/endpoints/auth/auth';
import { patchPersonaltrainerId } from '~/shared/api/generated/endpoints/personal-trainer/personal-trainer';
import { ApiError } from '~/shared/api/mutator';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';

import { accountRoot, trainerProfileQueryOptions } from '../api/accountQueries';
import {
  AVATAR_IDLE,
  avatarIsWorking,
  avatarRetryBytes,
  nextAvatarState,
  type AvatarEvent,
  type AvatarState,
} from '../model/avatarUpload';
import { profilePatch, profileSaveIsOffered, type ProfileOrigin } from '../model/profilePatch';
import { prepareAvatar, type AvatarPrepareFailure } from './avatarFile';
import { useSectionRead, type SectionState } from './useSectionRead';

export interface AccountProfileApi {
  readonly state: SectionState;
  /** Whether the wait belongs on screen. The code card's bar forks on this, not on `state`. */
  readonly waiting: boolean;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly onRetry: () => void;

  /** The trainer's own code, for the card beside the form. `null` when the backend sent none. */
  readonly trainerCode: string | null;
  readonly email: string;
  readonly initials: string;
  readonly avatarUrl: string | null;

  readonly firstName: string;
  readonly lastName: string;
  readonly onFirstNameChange: (value: string) => void;
  readonly onLastNameChange: (value: string) => void;
  /** Whether „Zapisz zmiany" acts. False while nothing changed, while saving, and before the read. */
  readonly saveIsOffered: boolean;
  readonly saving: boolean;
  readonly onSave: () => void;
  readonly saveError: string | null;

  /** The circle is working: the loader stands in it and the camera is refused. */
  readonly photoBusy: boolean;
  readonly photoError: string | null;
  /** Present exactly when a prepared image FAILED to upload and can be sent again as it is. */
  readonly onRetryPhoto: (() => void) | null;
  readonly onPickPhoto: (file: File) => void;
  readonly onRemovePhoto: () => void;

  readonly newPassword: string;
  readonly repeatPassword: string;
  readonly onNewPasswordChange: (value: string) => void;
  readonly onRepeatPasswordChange: (value: string) => void;
  readonly passwordAssessment: PasswordAssessment;
  readonly passwordSaveIsOffered: boolean;
  readonly passwordSaving: boolean;
  readonly onSavePassword: () => void;
  readonly passwordError: string | null;

  /** Everything worth saying out loud — „Zapisano", „Skopiowano", „Hasło zmienione". */
  readonly announcement: string;
  readonly announce: (message: string) => void;
}

const PASSWORD_MESSAGE_KEY: Readonly<Record<Exclude<PasswordUpdateOutcome, 'ok'>, string>> = {
  weakPassword: 'web:account.password.weak',
  samePassword: 'web:account.password.same',
  reauthenticationRequired: 'web:account.password.reauth',
  unknown: 'web:account.password.failed',
};

const PHOTO_MESSAGE_KEY: Readonly<Record<AvatarPrepareFailure, string>> = {
  badType: 'web:account.photo.badType',
  tooLarge: 'web:account.photo.tooLarge',
  tooManyPixels: 'web:account.photo.tooManyPixels',
  failed: 'web:account.photo.failed',
};

/**
 * @param active whether the Profil tab is the one on screen. The read is the trainer's own row and
 *   only that tab and the card beside it draw it, so a trainer sitting on Aplikacja makes no
 *   request at all — which is also the rule `/raporty` follows, one tab one set of reads.
 */
export function useAccountProfile(active: boolean): AccountProfileApi {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useSessionUser();
  const trainerId = active ? (user?.trainerId ?? null) : null;
  const { message: announcement, announce } = useAnnouncer();

  const read = useQuery(trainerProfileQueryOptions(trainerId));

  // A session carrying no trainer row is a failure no query reports: there is nothing to ask for.
  const section = useSectionRead({
    active: trainerId !== null,
    reads: [read],
    failed: trainerId === null && active,
  });

  const origin: ProfileOrigin = {
    firstName: read.data?.name ?? '',
    lastName: read.data?.surname ?? '',
  };

  /**
   * What the trainer has typed, or `null` while they have typed nothing.
   *
   * `null` rather than seeding from the read: a draft seeded in an effect would be overwritten the
   * next time the query refetched, mid-sentence. Absent means "whatever the read says", so the
   * fields fill themselves when the answer lands and stop following it the moment a key is pressed.
   */
  const [draft, setDraft] = useState<{ firstName: string; lastName: string } | null>(null);
  const form = draft ?? origin;

  const [saveError, setSaveError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const patch = profilePatch(form, origin);
      if (patch === null || trainerId === null) return;
      await patchPersonaltrainerId(trainerId, patch);
      // The rail draws the trainer's name off the session, which is zustand state rather than a
      // query — invalidating a key alone would leave it showing the old one.
      await rehydrateSession();
      await queryClient.invalidateQueries({ queryKey: accountRoot });
    },
    onSuccess: () => {
      // The draft is dropped rather than kept: what the read now says IS what was saved, and a
      // draft left standing would look dirty against its own answer.
      setDraft(null);
      setSaveError(null);
      announce(t('web:account.profile.saved'));
    },
    onError: () => {
      setSaveError(t('web:account.profile.saveFailed'));
    },
  });

  /* ── The photograph ─────────────────────────────────────────────────────────────────────── */

  const [photoError, setPhotoError] = useState<string | null>(null);
  /**
   * What the circle is doing, as ONE state — `model/avatarUpload`. The three booleans it replaces
   * are what produced the premature retry link: the prepared bytes are kept so a retry can send
   * them again, and „Spróbuj jeszcze raz" was drawn for as long as bytes were RETAINED rather than
   * for as long as the upload had actually failed.
   */
  const [photo, setPhoto] = useState<AvatarState>(AVATAR_IDLE);
  const step = (event: AvatarEvent) => {
    setPhoto((current) => nextAvatarState(current, event));
  };

  const afterPhotoWrite = async () => {
    await rehydrateSession();
    await queryClient.invalidateQueries({ queryKey: accountRoot });
  };

  const uploadPhoto = useMutation({
    /**
     * The rehydration is INSIDE the mutation rather than after it, and that is what makes the
     * hand-over flicker-free without an optimistic blob: the busy state does not end until
     * `/auth/me` has answered with the new address, so the loader is replaced by the photograph
     * itself and never by the initials in between. An object URL over the just-cropped bytes would
     * reach the screen a few frames sooner at the cost of a second source of truth for the face and
     * a revoke to get wrong.
     */
    mutationFn: async (blob: Blob) => {
      await putAuthMeAvatar({ file: blob });
      await afterPhotoWrite();
    },
    onSuccess: () => {
      step({ kind: 'uploaded' });
      setPhotoError(null);
      announce(t('web:account.photo.saved'));
    },
    onError: (error: unknown) => {
      step({ kind: 'uploadFailed' });
      // Branch on the CODE, never on the status: the media module answers 400 for four different
      // reasons and 429 for one, and only the coded ones can be worded usefully.
      const code = error instanceof ApiError ? error.code : undefined;
      const status = error instanceof ApiError ? error.status : 0;
      if (status === 429) {
        setPhotoError(t('web:account.photo.tooMany'));
      } else if (code === 'media_too_large') {
        setPhotoError(t('web:account.photo.tooLarge'));
      } else if (code === 'media_unsupported_type' || code === 'media_content_mismatch') {
        setPhotoError(t('web:account.photo.badType'));
      } else {
        setPhotoError(t('web:account.photo.failed'));
      }
    },
  });

  const removePhoto = useMutation({
    mutationFn: async () => {
      await deleteAuthMeAvatar();
      await afterPhotoWrite();
    },
    onSuccess: () => {
      setPhoto(AVATAR_IDLE);
      setPhotoError(null);
      announce(t('web:account.photo.removed'));
    },
    onError: () => {
      setPhotoError(t('web:account.photo.failed'));
    },
  });

  const retryBytes = avatarRetryBytes(photo);

  /* ── The password ───────────────────────────────────────────────────────────────────────── */

  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const passwordAssessment = assessPassword(newPassword, repeatPassword);

  const savePassword = useMutation({
    mutationFn: () => updatePassword(newPassword),
    onSuccess: (outcome) => {
      if (outcome !== 'ok') {
        setPasswordError(t(PASSWORD_MESSAGE_KEY[outcome]));
        return;
      }
      setNewPassword('');
      setRepeatPassword('');
      setPasswordError(null);
      announce(t('web:account.password.saved'));
    },
    onError: () => {
      setPasswordError(t('web:account.password.failed'));
    },
  });

  return {
    ...section,

    trainerCode: read.data?.trainerCode ?? null,
    email: read.data?.email ?? user?.email ?? '',
    // The session's own name while the read is still open, so the disc holds the trainer's letters
    // from the first frame rather than the „?" an empty name resolves to and then swapping.
    initials: `${form.firstName} ${form.lastName}`.trim() || (user?.displayName ?? ''),
    avatarUrl: user?.avatarUrl ?? null,

    firstName: form.firstName,
    lastName: form.lastName,
    onFirstNameChange: (value) => {
      setDraft({ ...form, firstName: value });
    },
    onLastNameChange: (value) => {
      setDraft({ ...form, lastName: value });
    },
    saveIsOffered: section.state === 'ready' && !save.isPending && profileSaveIsOffered(form, origin),
    saving: save.isPending,
    onSave: () => {
      save.mutate();
    },
    saveError,

    // The removal is its own act and not a phase of the upload, but it turns the same circle over,
    // so it draws the same loader and refuses the same camera.
    photoBusy: avatarIsWorking(photo) || removePhoto.isPending,
    photoError,
    onRetryPhoto:
      retryBytes === null
        ? null
        : () => {
            step({ kind: 'retry' });
            uploadPhoto.mutate(retryBytes);
          },
    onPickPhoto: (file) => {
      setPhotoError(null);
      step({ kind: 'pick' });
      void prepareAvatar(file).then((result) => {
        if (!result.ok) {
          step({ kind: 'prepareFailed' });
          setPhotoError(t(PHOTO_MESSAGE_KEY[result.reason]));
          return;
        }
        step({ kind: 'prepared', blob: result.blob });
        uploadPhoto.mutate(result.blob);
      });
    },
    onRemovePhoto: () => {
      setPhotoError(null);
      removePhoto.mutate();
    },

    newPassword,
    repeatPassword,
    onNewPasswordChange: (value) => {
      setNewPassword(value);
      setPasswordError(null);
    },
    onRepeatPasswordChange: (value) => {
      setRepeatPassword(value);
      setPasswordError(null);
    },
    passwordAssessment,
    passwordSaveIsOffered: passwordAssessment.acceptable && !savePassword.isPending,
    passwordSaving: savePassword.isPending,
    onSavePassword: () => {
      savePassword.mutate();
    },
    passwordError,

    announcement,
    announce,
  };
}
